// Global
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useRef, useState } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';

import classNames from 'classnames';
import { ComponentProps } from 'lib/types/component-props';

import styles from './TechDocChatBot.module.css';

// Add console logging for debugging
if (typeof window !== 'undefined') {
  console.log('[TechDocChatBot] Component loaded and ready');
}

export type TechDocChatbotProps =
  Feature.EnterpriseWeb.AndersenWindows.Components.Tool.TechDocChatbot.TechDocChatbot &
    ComponentProps;

const formatPages = (pages: SourceRef['page_numbers']) => {
  if (Array.isArray(pages)) {
    return pages.join(', ');
  }
  if (typeof pages === 'string') {
    return pages;
  }
  return '';
};

/**
 * Convert markdown-style text to HTML with styled document links
 * - Converts **text** to <strong>text</strong>
 * - Enhances backend-generated download links with Figma-designed styling (download icon)
 */
const formatResponseText = (text: string): string => {
  // First, convert markdown bold **text** to <strong>text</strong>
  let html = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Download icon SVG per Figma design
  const downloadIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none" style="display:inline-block;vertical-align:middle;margin-left:6px;"><path d="M5.53125 10L5.53125 1" stroke="#ED6833" stroke-width="1.5"/><path d="M10.4297 4.94922L5.47994 9.89897L0.530193 4.94922" stroke="#ED6833" stroke-width="1.5"/><path d="M0.03125 13H11.0312" stroke="#ED6833" stroke-width="2"/></svg>`;

  // Enhance backend-generated <a> tags that link to /downloadfile with the download icon
  // Backend generates: <a href="...downloadfile?filename=doc.pdf" ...>doc.pdf</a>
  // We add the download icon and document-link class for styling
  html = html.replace(
    /<a\s+href="([^"]*\/downloadfile\?filename=[^"]+)"([^>]*)>([^<]+)<\/a>/gi,
    (_match, url, attrs, linkText) => {
      return `<a href="${url}" class="document-link"${attrs}>${linkText}${downloadIcon}</a>`;
    }
  );

  return html;
};

interface SourceRef {
  file_name?: string | null;
  page_numbers?: number[] | string | null;
  raw_text?: string | null;
  url?: string | null;
}

interface BetaFeedbackData {
  rating: number;
  responses: { question_id: number; answer_text: string }[];
}

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  sources?: SourceRef[];
  backendMessageId?: number;
  backendSessionId?: number;
  feedback?: 'up' | 'down' | undefined;
  feedbackPending?: boolean;
  feedbackReason?: string | null;
  feedbackAckText?: string | null;
  isThinking?: boolean;
  betaFeedback?: BetaFeedbackData;
  fadingAck?: boolean;
}

interface FeedbackQuestion {
  id: number;
  text: string;
  placeholder?: string;
  key?: string; // 'sources', 'accuracy', etc.
}

const TechDocChatbot = (props: TechDocChatbotProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [sessionId, setSessionId] = useState<number | undefined>(undefined);
  const INTRO_MESSAGE =
    "Hello! I'm your TechDoc assistant. I can help you with questions about Andersen Windows & Doors products, technical documentation, and installation procedures. What would you like to know?";
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: INTRO_MESSAGE,
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const SHOW_SOURCES = false;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [questions, setQuestions] = useState<FeedbackQuestion[]>([]);
  // Per Figma specs: stop at 200 characters
  const MAX_INPUT_LENGTH = 200;
  const FEEDBACK_ACK_TEXT = 'Thank you, your feedback helps us improve!';
  const FIND_DOCS_URL = '/technical-documents/';
  const CONTACT_EXPERT_URL = '/support/contact-us/';

  const [feedbackModal, setFeedbackModal] = useState<{
    open: boolean;
    messageId?: string;
    rating: number;
    accuracy: string;
    sources: string;
    completeness: string;
    relevance: string;
    answers: Record<number, string>;
  }>({
    open: false,
    messageId: undefined,
    rating: 0,
    accuracy: '',
    sources: '',
    completeness: '',
    relevance: '',
    answers: {},
  });

  /*const [feedbackModalOld, setFeedbackModalOld] = useState<{
    open: boolean;
    messageId?: string;
    text: string;
  }>({ open: false, messageId: undefined, text: '' });*/

  const [me, setMe] = useState<{
    username: string;
    email?: string | null;
    display_name?: string | null;
    auth_provider?: string;
    roles?: string[] | null;
  } | null>(null);

  const [authModal, setAuthModal] = useState<{
    open: boolean;
    mode: 'login' | 'register';
    username: string;
    email: string;
    displayName: string;
    password: string;
    busy: boolean;
    error: string | null;
  }>({
    open: false,
    mode: 'login',
    username: '',
    email: '',
    displayName: '',
    password: '',
    busy: false,
    error: null,
  });

  const SHOW_AUTH_UI = false;

  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
  const [hoverState, setHoverState] = useState<{ id: string; rating: number } | null>(null);

  async function loadMe() {
    try {
      const resp = await fetch('/api/auth/me');
      if (!resp.ok) {
        setMe(null);
        return;
      }
      const data = await resp.json();
      setMe(data);
    } catch {
      setMe(null);
    }
  }

  useEffect(() => {
    loadMe();
  }, []);

  useEffect(() => {
    async function fetchQuestions() {
      try {
        const res = await fetch('/api/ai/feedback-questions');
        if (res.ok) {
          const data = await res.json();
          setQuestions(data);
        }
      } catch (e) {
        console.error('Failed to load feedback questions', e);
      }
    }
    fetchQuestions();
  }, []);

  function resetChat() {
    setSessionId(undefined);
    setMessages([
      {
        id: '1',
        text: INTRO_MESSAGE,
        isUser: false,
        timestamp: new Date(),
      },
    ]);
  }

  type Me = {
    username: string;
    email?: string | null;
    display_name?: string | null;
    auth_provider?: string;
    roles?: string[] | null;
  } | null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function accountLabel(user: Me): string {
    if (!user) {
      return 'Guest';
    }
    const username = (user.username || '').trim();
    const displayName = (user.display_name || '').trim();
    if (user.auth_provider === 'entra' && username.includes('@')) {
      return username.split('@')[0];
    }
    const u = username || displayName;
    if (!u) {
      return 'Guest';
    }
    return u.length > 6 ? `${u.slice(0, 6)}...` : u;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function openAuthModalDefault() {
    setAuthModal({
      open: true,
      mode: 'login',
      username: '',
      email: '',
      displayName: '',
      password: '',
      busy: false,
      error: null,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function hasRole(user: Me, role: string): boolean {
    if (!user || !Array.isArray(user.roles)) {
      return false;
    }
    return user.roles.includes(role);
  }

  async function doLogin() {
    setAuthModal((s) => ({ ...s, busy: true, error: null }));
    try {
      const resp = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: authModal.username.trim(),
          password: authModal.password,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setAuthModal((s) => ({
          ...s,
          busy: false,
          error: data?.detail || data?.error || 'Login failed',
        }));
        return;
      }
      await loadMe();
      setAuthModal((s) => ({ ...s, open: false, busy: false }));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setAuthModal((s) => ({ ...s, busy: false, error: String(e?.message ?? e) }));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function doRegister() {
    setAuthModal((s) => ({ ...s, busy: true, error: null }));
    try {
      const resp = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: authModal.username.trim(),
          email: authModal.email.trim() || null,
          password: authModal.password,
          display_name: authModal.displayName.trim() || null,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setAuthModal((s) => ({
          ...s,
          busy: false,
          error: data?.detail || data?.error || 'Registration failed',
        }));
        return;
      }
      await doLogin();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setAuthModal((s) => ({ ...s, busy: false, error: String(e?.message ?? e) }));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function doLogout() {
    try {
      await fetch('/api/auth/logout');
    } catch {}
    setMe(null);
    resetChat();
  }

  // Detect mobile/tablet/desktop with debounce for performance
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkViewport, 150);
    };

    checkViewport();
    window.addEventListener('resize', debouncedCheck);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedCheck);
    };
  }, []);

  /*async function postFeedback(opts: {
    messageId: number;
    isPositive: boolean;
    commentText?: string | null;
    reasonCode?: string | null;
  }) {
    const body = {
      message_id: opts.messageId,
      is_positive: opts.isPositive,
      comment_text: opts.commentText ?? null,
      reason_code: opts.reasonCode ?? null,
      session_id: sessionId ?? null,
    };

    const resp = await fetch('/api/ai/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (!resp.ok) {
      throw new Error(data?.detail || data?.error || `Feedback failed (${resp.status})`);
    }
    return data;
  }*/

  async function postBetaFeedback(opts: {
    messageId: number;
    rating: number;
    responses: { question_id: number; answer_text: string }[];
  }) {
    const body = {
      message_id: opts.messageId,
      session_id: sessionId ?? null,
      rating: opts.rating,
      responses: opts.responses,
    };

    const resp = await fetch('/api/ai/feedback-beta', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      throw new Error('Beta feedback failed');
    }
    return await resp.json();
  }

  function setMessage(idx: number, patch: Partial<ChatMessage>) {
    setMessages((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  function patchMessageById(id: string, patch: Partial<ChatMessage>) {
    setMessages((prev) => {
      const i = prev.findIndex((m) => m.id === id);
      if (i === -1) {
        return prev;
      }
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }

  const handleRatingClick = (idx: number, rating: number) => {
    const m = messages[idx];
    if (!m.backendMessageId) {
      return;
    }

    // 1. Prepare initial answers
    const initialAnswers: Record<number, string> = {};

    // 2. Find the "sources" question dynamically
    const sourcesQuestion = questions.find((q) => q.key === 'sources');

    // 3. If found and message has sources, pre-fill it
    if (sourcesQuestion && m.sources && m.sources.length > 0) {
      const sourceText = m.sources
        .map((s) => {
          const name = s.file_name || 'Document';
          const pages = formatPages(s.page_numbers);
          return pages ? `${name} (Page ${pages})` : name;
        })
        .join('\n');

      initialAnswers[sourcesQuestion.id] = sourceText;
    }

    // 2. Set it in the state
    setFeedbackModal({
      open: true,
      messageId: m.id,
      rating: rating,
      accuracy: '',
      sources:
        sourcesQuestion && typeof sourcesQuestion.id === 'number'
          ? initialAnswers[sourcesQuestion.id] || ''
          : '',
      completeness: '',
      relevance: '',
      answers: initialAnswers,
    });
  };

  async function submitBetaFeedback() {
    const id = feedbackModal.messageId!;
    const idx = messages.findIndex((x) => x.id === id);
    const m = messages[idx];

    const responseArray = Object.entries(feedbackModal.answers).map(([qId, text]) => ({
      question_id: parseInt(qId),
      answer_text: text,
    }));

    try {
      setMessage(idx, { feedbackPending: true });

      // ... API Call (postBetaFeedback) ...
      await postBetaFeedback({
        messageId: m.backendMessageId!,
        rating: feedbackModal.rating,
        responses: responseArray,
      });

      // 1. Show the "Thank You" text and save the data
      setMessage(idx, {
        feedbackPending: false,
        feedbackAckText: FEEDBACK_ACK_TEXT,
        betaFeedback: {
          rating: feedbackModal.rating,
          responses: responseArray,
        },
        fadingAck: false, // Ensure fully visible initially
      });

      // 2. Wait 2 seconds, then trigger fade out
      setTimeout(() => {
        setMessage(idx, { fadingAck: true });

        // 3. Wait 500ms (match CSS transition), then remove text to show stars
        setTimeout(() => {
          setMessage(idx, { feedbackAckText: undefined, fadingAck: false });
        }, 500);
      }, 2000);
    } catch (e) {
      console.error(e);
      setMessage(idx, { feedbackPending: false });
    } finally {
      setFeedbackModal((prev) => ({ ...prev, open: false }));
    }
  }

  /*async function handleThumbUp(idx: number) {
    const m = messages[idx];
    if (!m.backendMessageId) {
      return;
    }

    try {
      setMessage(idx, { feedbackPending: true });
      await postFeedback({
        messageId: m.backendMessageId,
        isPositive: true,
        commentText: null,
        reasonCode: null,
      });
      setMessage(idx, {
        feedback: 'up',
        feedbackPending: false,
        feedbackReason: null,
        feedbackAckText: FEEDBACK_ACK_TEXT,
      });
    } catch (e) {
      console.error(e);
      setMessage(idx, { feedbackPending: false });
    }
  }

  function handleThumbDown(idx: number) {
    const m = messages[idx];
    if (!m?.backendMessageId) {
      return;
    }
    setFeedbackModalOld({ open: true, messageId: m.id, text: m?.feedbackReason ?? '' });
  }

  async function submitThumbDown(withoutExplanation = false) {
    const id = feedbackModalOld.messageId!;
    const idx = messages.findIndex((x) => x.id === id);
    if (idx < 0) {
      return;
    }
    const m = messages[idx];
    if (!m?.backendMessageId) {
      return;
    }

    try {
      setMessage(idx, { feedbackPending: true });
      const explanation = withoutExplanation ? null : feedbackModalOld.text.trim() || null;
      await postFeedback({
        messageId: m.backendMessageId,
        isPositive: false,
        reasonCode: null,
        commentText: explanation,
      });
      setMessage(idx, {
        feedback: 'down',
        feedbackPending: false,
        feedbackReason: explanation,
        feedbackAckText: FEEDBACK_ACK_TEXT,
      });
    } catch (e) {
      console.error(e);
      setMessage(idx, { feedbackPending: false });
    } finally {
      setFeedbackModalOld({ open: false, messageId: undefined, text: '' });
    }
  }*/

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if (!inputText.trim()) {
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputText.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const question = inputText.trim();
    setInputText('');
    setIsLoading(true);

    const thinkingId = `thinking-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: thinkingId,
        text: 'Thinking...',
        isUser: false,
        timestamp: new Date(),
        isThinking: true,
      },
    ]);

    try {
      const payload = { question, verbose: false, session_id: sessionId ?? null };
      console.log('POST /ai/answer payload', payload);

      const resp = await fetch('/api/ai/answer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await resp.json();
      if (!resp.ok) {
        const errMsg = data?.detail || data?.error || `Upstream error (${resp.status})`;
        throw new Error(errMsg);
      }

      if (!sessionId || sessionId !== data.session_id) {
        setSessionId(data.session_id);
      }

      let responseText = data?.answer || 'No response content.';
      // Keep the (Source: ...) citation - it's a requirement for every RAG response
      // Convert markdown to HTML and create document links
      responseText = formatResponseText(responseText);

      const sourceDocs = Array.isArray(data?.sources)
        ? data.sources
        : Array.isArray(data?.source_documents)
        ? data.source_documents
        : [];

      patchMessageById(thinkingId, {
        id: (Date.now() + 1).toString(),
        text: responseText,
        isThinking: false,
        sources: sourceDocs,
        backendMessageId: data.message_id,
        backendSessionId: data.session_id,
      });
    } catch (error) {
      console.error('API Error:', error);
      patchMessageById(thinkingId, {
        text: `Backend Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isThinking: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClose = () => {
    setExitConfirmOpen(true);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsOpen(false);
  };

  const confirmClose = () => {
    setExitConfirmOpen(false);
    setIsOpen(false);
    setIsMinimized(false);
    resetChat();
  };

  const cancelClose = () => {
    setExitConfirmOpen(false);
  };

  const handleExpand = () => {
    if (isMinimized) {
      setIsMinimized(false);
      setIsOpen(true);
    } else {
      setIsOpen(true);
    }
  };

  if (!props.fields) {
    return <></>;
  }

  // Responsive chatbot dimensions and positioning
  // Per Figma specs: Desktop width = 480px (1/3rd of 1440px standard)
  // Header height = 64px, Icons = 24x24px, 8-pixel grid system
  const getChatDimensions = () => {
    if (isMobile) {
      return {
        width: '100vw',
        height: '100vh',
        bottom: 0,
        right: 0,
        top: 'auto',
        borderRadius: 0,
      };
    }
    if (isTablet) {
      return {
        width: '400px',
        height: '600px',
        bottom: '24px',
        right: '24px',
        top: 'auto',
        borderRadius: '16px',
      };
    }
    // Desktop: Fixed 480x600px per Figma specs, positioned bottom-right
    return {
      width: '480px',
      height: '600px',
      bottom: '24px',
      right: '24px',
      top: 'auto',
      borderRadius: '16px',
    };
  };

  const chatDimensions = getChatDimensions();

  return (
    <div className={styles.chatbotRoot}>
      {/* Floating Chat Button */}
      {!isOpen && !isMinimized && (
        <button onClick={handleExpand} className={styles.floatingButton} aria-label="Open chat">
          <img src="/chatbot-icon.svg" alt="ChatBot" className={styles.buttonIcon} />
        </button>
      )}

      {/* Minimized Chat Header */}
      {isMinimized && (
        <div onClick={handleExpand} className={styles.minimizedHeader}>
          <div className={styles.minimizedContent}>
            <div className={styles.minimizedLeft}>
              <div className={styles.minimizedIconWrapper}>
                <img src="/chatbot-icon.svg" alt="ChatBot" className={styles.minimizedIcon} />
              </div>
              <span className={styles.minimizedTitle}>Tech Doc ChatBot</span>
            </div>
            <svg
              className={styles.expandIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
            >
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </div>
        </div>
      )}

      {/* Main Chat Window */}
      {isOpen && (
        <div
          className={styles.chatbotContainer}
          style={{
            width: chatDimensions.width,
            height: chatDimensions.height,
            bottom: chatDimensions.bottom,
            right: chatDimensions.right,
            top: chatDimensions.top,
            borderRadius: chatDimensions.borderRadius,
          }}
        >
          {/* Chat Header */}
          <div className={styles.chatHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIconWrapper}>
                <img src="/chatbot-icon.svg" alt="ChatBot" className={styles.headerIcon} />
              </div>
              <h3 className={styles.headerTitle}>Tech Doc ChatBot</h3>
            </div>

            <div className={styles.headerRight}>
              {/* Minimize (desktop only) - Per Figma */}
              {!isMobile && (
                <button
                  onClick={handleMinimize}
                  className={styles.headerButton}
                  aria-label="Minimize"
                >
                  <svg className={styles.headerButtonIcon} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 21V19H18V21H6Z" />
                  </svg>
                </button>
              )}

              {/* Close - Per Figma */}
              <button onClick={handleClose} className={styles.headerButton} aria-label="Close">
                <svg className={styles.headerButtonIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z" />
                </svg>
              </button>
            </div>
          </div>

          {/* AI Disclaimer */}
          <div className={styles.disclaimer}>
            AI-generated responses.{' '}
            <span className={styles.disclaimerLink} onClick={() => setShowMoreInfoModal(true)}>
              More information
            </span>
          </div>

          {/* Messages Area */}
          <div className={styles.messagesArea}>
            {messages.map((message, idx) => (
              <div key={message.id} className={styles.messageContainer}>
                <div
                  className={classNames(
                    styles.messageRow,
                    message.isUser ? styles.messageRowUser : styles.messageRowBot
                  )}
                >
                  <div className={styles.messageContent}>
                    <div
                      className={classNames(
                        styles.messageBubble,
                        message.isUser ? styles.messageBubbleUser : styles.messageBubbleBot
                      )}
                    >
                      {message.isThinking ? (
                        <div className={styles.thinkingDots}>
                          <span className={styles.thinkingDot}></span>
                          <span className={styles.thinkingDot}></span>
                          <span className={styles.thinkingDot}></span>
                          <span className={styles.thinkingDot}></span>
                        </div>
                      ) : (
                        <p
                          className={styles.messageText}
                          dangerouslySetInnerHTML={{ __html: message.text }}
                        />
                      )}

                      {/* Source Documents */}
                      {SHOW_SOURCES && !message.isUser && message.sources?.length ? (
                        <div className={styles.sourcesContainer}>
                          <div className={styles.sourcesScroll}>
                            {message.sources.map((s, sIdx) => (
                              <a
                                key={sIdx}
                                href={s.url || '#'}
                                download={s.file_name || 'document.pdf'}
                                rel="noopener noreferrer"
                                className={styles.sourceCard}
                              >
                                <div className={styles.sourceCardContent}>
                                  <div className={styles.sourceIconWrapper}>
                                    <svg
                                      className={styles.sourceIcon}
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#ED6833"
                                      strokeWidth="1.5"
                                    >
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                      <polyline points="14 2 14 8 20 8" />
                                      <text
                                        x="7"
                                        y="17"
                                        fontSize="6"
                                        fill="#ED6833"
                                        fontWeight="bold"
                                      >
                                        PDF
                                      </text>
                                    </svg>
                                    <div className={styles.sourceDownloadBadge}>
                                      <svg
                                        className={styles.sourceDownloadIcon}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        strokeWidth="3"
                                      >
                                        <path d="M12 5v14M19 12l-7 7-7-7" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className={styles.sourceFileName}>
                                    {s.file_name || 'Document'}
                                  </div>
                                  {s.page_numbers && (
                                    <div className={styles.sourcePageNumbers}>
                                      Page {formatPages(s.page_numbers)}
                                    </div>
                                  )}
                                </div>
                              </a>
                            ))}
                            {message.sources.length > 2 && (
                              <div className={styles.sourcesNavArrow}>
                                <div className={styles.sourcesNavButton}>
                                  <svg
                                    className={styles.sourcesNavIcon}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    strokeWidth="2"
                                  >
                                    <polyline points="9 18 15 12 9 6" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {/*!message.isUser && !message.isThinking && message.backendMessageId && (
                      <div className={styles.feedbackSection}>
                        {!message.feedback && !message.feedbackAckText && (
                          <>
                            <span className={styles.feedbackLabel}>Was this helpful?</span>
                            <button
                              aria-label="Thumbs up"
                              aria-pressed={message.feedback === 'up'}
                              onClick={() => handleThumbUp(idx)}
                              disabled={message.feedbackPending}
                              className={classNames(
                                styles.feedbackButton,
                                message.feedbackPending && styles.feedbackButtonDisabled
                              )}
                            >
                              <svg
                                className={styles.feedbackIcon}
                                viewBox="0 0 22 22"
                                fill="#6b7280"
                              >
                                <path d="M16.5007 19.2493H7.33398V7.33268L13.7507 0.916016L14.8965 2.06185C15.0034 2.16879 15.0913 2.31393 15.16 2.49727C15.2288 2.6806 15.2632 2.85629 15.2632 3.02435V3.34518L14.2548 7.33268H19.2507C19.7395 7.33268 20.1673 7.51602 20.534 7.88268C20.9007 8.24935 21.084 8.67713 21.084 9.16602V10.9993C21.084 11.1063 21.0725 11.2209 21.0496 11.3431C21.0267 11.4653 20.9923 11.5799 20.9465 11.6868L18.1965 18.1494C18.059 18.4549 17.8298 18.7146 17.509 18.9285C17.1882 19.1424 16.852 19.2493 16.5007 19.2493ZM5.50065 7.33268V19.2493H1.83398V7.33268H5.50065Z" />
                              </svg>
                            </button>

                            <button
                              aria-label="Thumbs down"
                              aria-pressed={message.feedback === 'down'}
                              onClick={() => handleThumbDown(idx)}
                              disabled={message.feedbackPending}
                              className={classNames(
                                styles.feedbackButton,
                                message.feedbackPending && styles.feedbackButtonDisabled
                              )}
                            >
                              <svg
                                className={styles.feedbackIcon}
                                viewBox="0 0 22 22"
                                fill="#6b7280"
                              >
                                <path d="M5.49935 2.75065H14.666V14.6673L8.24935 21.084L7.10352 19.9382C6.99657 19.8312 6.90872 19.6861 6.83997 19.5027C6.77122 19.3194 6.73685 19.1437 6.73685 18.9757V18.6548L7.74518 14.6673H2.74935C2.26046 14.6673 1.83268 14.484 1.46601 14.1173C1.09935 13.7507 0.916016 13.3229 0.916016 12.834V11.0007C0.916016 10.8937 0.927473 10.7791 0.95039 10.6569C0.973307 10.5347 1.00768 10.4201 1.05351 10.3132L3.80351 3.85065C3.94102 3.54509 4.17018 3.28537 4.49101 3.07148C4.81185 2.8576 5.14796 2.75065 5.49935 2.75065ZM16.4993 14.6673V2.75065H20.166V14.6673H16.4993Z" />
                              </svg>
                            </button>
                          </>
                        )}
                        

                        {message.feedbackPending && (
                          <span className={styles.feedbackPending}>saving…</span>
                        )}

                        {!message.feedbackPending && message.feedbackAckText && (
                          <span className={styles.feedbackAck}>{message.feedbackAckText}</span>
                        )}
                      </div>
                    )*/}

                    {!message.isUser && !message.isThinking && message.backendMessageId && (
                      <div className={styles.feedbackSection}>
                        {/* SCENARIO 1: Show "Thank You" text */}
                        {!message.feedbackPending && message.feedbackAckText ? (
                          <span
                            className={classNames(
                              styles.feedbackAck,
                              message.fadingAck && styles.fadingOut
                            )}
                          >
                            {message.feedbackAckText}
                          </span>
                        ) : (
                          /* SCENARIO 2: Show Stars */
                          <>
                            <span className={styles.feedbackLabel}>
                              {message.betaFeedback ? 'You rated this:' : 'Rate this answer:'}
                            </span>

                            {/* Add onMouseLeave to the CONTAINER to reset when cursor leaves the star group */}
                            <div
                              className={styles.ratingStars}
                              onMouseLeave={() => setHoverState(null)}
                            >
                              {[1, 2, 3, 4, 5].map((star) => {
                                const isReadOnly = !!message.betaFeedback;
                                const isModalActive =
                                  feedbackModal.open && feedbackModal.messageId === message.id;
                                const isHovering = hoverState?.id === message.id;

                                // COLOR LOGIC:
                                // 1. If ReadOnly (submitted): use saved rating
                                // 2. If Hovering: use the star being hovered
                                // 3. If Modal is Open (clicked): use the rating in the modal
                                // 4. Default: 0 (empty)
                                let ratingToDisplay = 0;

                                if (isReadOnly) {
                                  ratingToDisplay = message.betaFeedback!.rating;
                                } else if (isHovering) {
                                  ratingToDisplay = hoverState!.rating;
                                } else if (isModalActive) {
                                  ratingToDisplay = feedbackModal.rating;
                                }

                                return (
                                  <button
                                    key={star}
                                    onClick={() => !isReadOnly && handleRatingClick(idx, star)}
                                    // Track hover entering specific stars
                                    onMouseEnter={() =>
                                      !isReadOnly && setHoverState({ id: message.id, rating: star })
                                    }
                                    disabled={isReadOnly}
                                    className={styles.starButton}
                                    type="button"
                                    aria-label={`${star} stars`}
                                  >
                                    <svg
                                      viewBox="0 0 24 24"
                                      // Fill based on the calculated priority
                                      fill={ratingToDisplay >= star ? '#ED6833' : 'none'}
                                      stroke="#ED6833"
                                      width="24"
                                      height="24"
                                    >
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamp */}
                <div
                  className={classNames(
                    styles.timestamp,
                    message.isUser ? styles.timestampUser : styles.timestampBot
                  )}
                >
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}

            <div aria-hidden style={{ height: 8 }} />
            <div ref={messagesEndRef} />
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtonsContainer}>
            <div className={styles.actionButtonsRow}>
              <a href={FIND_DOCS_URL} className={styles.actionButton}>
                Find another document
              </a>
              <a href={CONTACT_EXPERT_URL} className={styles.actionButton}>
                Contact an Expert
              </a>
            </div>
          </div>

          {/* Input Area */}
          <div className={styles.inputArea}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                value={inputText}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_INPUT_LENGTH) {
                    setInputText(e.target.value);
                  }
                }}
                onKeyDown={handleKeyPress}
                placeholder="Type your message here..."
                className={styles.chatInput}
                disabled={isLoading}
              />
              <span className={styles.charCounter}>/{MAX_INPUT_LENGTH}</span>
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isLoading}
                className={styles.sendButton}
                aria-label="Send message"
              >
                {/* Send icon - outlined triangle per Figma design */}
                <svg className={styles.sendButtonIcon} viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 4L20 12L6 20V4Z"
                    stroke={inputText.trim() ? '#ED6833' : '#D1D5DB'}
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Support Footer */}
          <div className={classNames(styles.supportFooter, isMobile && styles.safeAreaBottom)}>
            <p className={styles.supportFooterText}>
              Contact us at{' '}
              <a
                href="tel:18009333626"
                className={styles.supportLink}
                aria-label="Call Andersen support at 1 800 933 3626"
              >
                1-800-933-3626
              </a>
            </p>
          </div>

          {/* Feedback Modal */}
          {/*feedbackModal.open && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                <h4 className={styles.modalTitle}>Tell us what went wrong (optional)</h4>
                <textarea
                  value={feedbackModal.text}
                  onChange={(e) => setFeedbackModal((s) => ({ ...s, text: e.target.value }))}
                  placeholder="What was inaccurate, missing, or unhelpful?"
                  className={styles.modalTextarea}
                />
                <div className={styles.modalActions}>
                  <button
                    onClick={() =>
                      setFeedbackModal({ open: false, messageId: undefined, text: '' })
                    }
                    className={styles.modalButtonSecondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => submitThumbDown(true)}
                    className={styles.modalButtonSecondary}
                  >
                    Submit without explanation
                  </button>
                  <button
                    onClick={() => submitThumbDown(false)}
                    className={styles.modalButtonPrimary}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )*/}

          {feedbackModal.open && (
            <div className={styles.modalOverlay}>
              {/* 1. Changed maxWidth to 650px */}
              <div className={styles.modalContent} style={{ maxWidth: '650px' }}>
                <h4 className={styles.modalTitle}>Help us improve!</h4>
                <div className={styles.modalRatingWrapper}>
                  <span className={styles.modalRatingText}>You rated this:</span>
                  <div className={styles.ratingStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        // Allow user to correct the rating inside the modal
                        onClick={() => setFeedbackModal((s) => ({ ...s, rating: star }))}
                        className={styles.starButton}
                        type="button"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill={feedbackModal.rating >= star ? '#ED6833' : 'none'}
                          stroke="#ED6833"
                          width="24"
                          height="24"
                        >
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>

                {/* DYNAMIC QUESTIONS RENDER */}
                {questions.map((q) => (
                  <div key={q.id} className={styles.betaField}>
                    <label>{q.text}</label>
                    <textarea
                      value={feedbackModal.answers[q.id] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFeedbackModal((prev) => ({
                          ...prev,
                          answers: { ...prev.answers, [q.id]: val },
                        }));
                      }}
                      placeholder={q.placeholder || ''}
                      className={styles.modalTextarea}
                    />
                  </div>
                ))}

                <div className={styles.modalActions}>
                  <button
                    onClick={() => setFeedbackModal((s) => ({ ...s, open: false }))}
                    className={styles.modalButtonSecondary}
                  >
                    Cancel
                  </button>
                  <button onClick={submitBetaFeedback} className={styles.modalButtonPrimary}>
                    Submit Feedback
                  </button>
                </div>
              </div>
            </div>
          )}

          {exitConfirmOpen && (
            <div className={styles.modalOverlay}>
              <div className={styles.exitConfirmContent}>
                <button
                  onClick={cancelClose}
                  className={styles.exitConfirmCloseButton}
                  aria-label="Close"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                    <path d="M9 9l6 6M15 9l-6 6" />
                  </svg>
                </button>
                <p className={styles.exitConfirmText}>
                  Are you sure you want to end the conversation?
                </p>
                <div className={styles.exitConfirmActions}>
                  <button onClick={confirmClose} className={styles.exitConfirmYes}>
                    Yes
                  </button>
                  <button onClick={cancelClose} className={styles.exitConfirmNo}>
                    No
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* More Information Modal */}
          {showMoreInfoModal && (
            <div
              className={styles.moreInfoOverlay}
              style={{
                width: chatDimensions.width,
                height: chatDimensions.height,
                bottom: chatDimensions.bottom,
                right: chatDimensions.right,
                borderRadius: chatDimensions.borderRadius,
              }}
              onClick={() => setShowMoreInfoModal(false)}
            >
              <div className={styles.moreInfoContent} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setShowMoreInfoModal(false)}
                  className={styles.moreInfoCloseButton}
                  aria-label="Close"
                >
                  <svg className={styles.moreInfoCloseIcon} viewBox="0 0 24 24" strokeWidth="2.5">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>

                <h2 className={styles.moreInfoTitle}>More Information</h2>

                <p className={styles.moreInfoText}>
                  Please be advised that this chat may be monitored, copied, stored, or recorded and
                  that by communicating and providing personal information, you agree to our privacy
                  policy.
                </p>

                <a
                  href="https://www.andersenwindows.com/support/privacy"
                  className={styles.moreInfoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Statement
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Auth UI (hidden by default) */}
      {SHOW_AUTH_UI && authModal.open && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalTitle}>
              {authModal.mode === 'login' ? 'Sign in' : 'Create your account'}
            </div>
            {/* Auth form content would go here */}
          </div>
        </div>
      )}

      {SHOW_AUTH_UI && accountModalOpen && me && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalTitle}>Account</div>
            {/* Account modal content would go here */}
          </div>
        </div>
      )}
    </div>
  );
};

export default withDatasourceCheck()<TechDocChatbotProps>(TechDocChatbot);
